import React, { useCallback, useRef, useState } from 'react'
import { View, ActivityIndicator } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Icon, Text } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { showSuccessToast } from '@utils'
import { createGhostProfile } from '@hooks'
import {
  CanonicalField,
  ParsedFile,
  parseContactsFile,
  rowToGhostProfile,
  UNSUPPORTED_FORMAT_ERROR,
} from './parseContactsFile'

// "Import from a CSV file" block for the web import screen. The user drops a
// .csv export; we auto-detect the columns, preview how many contacts will be
// created, then create one ghost profile per row.
// CSV-only: the previous Excel (.xlsx) path relied on the `xlsx`/SheetJS library
// which is vulnerable to CVE-2023-30533 / CVE-2024-22363 with no patched build,
// so binary uploads are now rejected.

type Phase = 'idle' | 'parsed' | 'importing' | 'done'

// Human labels (FR fallback) used for the "detected columns" summary chips.
const FIELD_LABEL_KEYS: Record<CanonicalField, string> = {
  firstName: 'importContacts.firstName',
  lastName: 'importContacts.lastName',
  fullName: 'importContacts.file.fullName',
  email: 'importContacts.email',
  phone: 'importContacts.phone',
  company: 'importContacts.company',
  jobTitle: 'importContacts.profession',
  website: 'importContacts.website',
  linkedin: 'importContacts.file.linkedin',
  instagram: 'importContacts.file.instagram',
  facebook: 'importContacts.file.facebook',
  twitter: 'importContacts.file.twitter',
  notes: 'importContacts.file.notes',
}

export const FileImportSection: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const queryClient = useQueryClient()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParsedFile | null>(null)
  const [fileName, setFileName] = useState('')
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [result, setResult] = useState({ created: 0, skipped: 0, failed: 0 })

  const validRowCount = parsed
    ? parsed.rows.filter((r) => rowToGhostProfile(r, parsed.mapping)).length
    : 0
  const detectedFields = parsed
    ? (parsed.mapping.filter(Boolean) as CanonicalField[])
    : []

  const handleFile = useCallback(
    async (file: File) => {
      setError(null)
      const name = (file.name || '').toLowerCase()
      const isCsv = name.endsWith('.csv') || file.type === 'text/csv'
      if (!isCsv) {
        setError(t('importContacts.file.unsupportedFormat'))
        return
      }
      try {
        const result = await parseContactsFile(file)
        if (result.rows.length === 0) {
          setError(t('importContacts.file.empty'))
          return
        }
        setParsed(result)
        setFileName(file.name)
        setPhase('parsed')
      } catch (e: any) {
        // Map the parser's "unsupported format" sentinel to its translated copy;
        // any other failure falls back to the generic parse error.
        const key =
          e?.message === UNSUPPORTED_FORMAT_ERROR
            ? 'importContacts.file.unsupportedFormat'
            : 'importContacts.file.parseError'
        setError(t(key))
      }
    },
    [t],
  )

  const handleImport = useCallback(async () => {
    if (!parsed) return
    const payloads = parsed.rows
      .map((r) => rowToGhostProfile(r, parsed.mapping))
      .filter(Boolean) as ReturnType<typeof rowToGhostProfile>[]

    const skipped = parsed.rows.length - payloads.length
    setPhase('importing')
    setProgress({ done: 0, total: payloads.length })

    let created = 0
    let failed = 0
    for (let i = 0; i < payloads.length; i++) {
      try {
        await createGhostProfile(payloads[i]!)
        created++
      } catch {
        failed++
      }
      setProgress({ done: i + 1, total: payloads.length })
    }

    await queryClient.invalidateQueries({ queryKey: ['contacts'] })
    setResult({ created, skipped, failed })
    setPhase('done')
    if (created > 0) {
      showSuccessToast(t('toast.success'), t('importContacts.file.importedToast', { count: created }))
    }
  }, [parsed, queryClient, t])

  const reset = () => {
    setPhase('idle')
    setParsed(null)
    setFileName('')
    setError(null)
    setProgress({ done: 0, total: 0 })
    setResult({ created: 0, skipped: 0, failed: 0 })
  }

  return (
    <View style={styles.wrapper}>
      <View style={styles.titleRow}>
        <Icon name="upload" size={theme.iconSize.medium} color={theme.colors.primary} />
        <Text type="bodyBold" color={theme.colors.black}>
          {t('importContacts.file.title')}
        </Text>
      </View>
      <Text type="bodySmall" color={theme.colors.lightGrey} style={styles.subtitle}>
        {t('importContacts.file.subtitle')}
      </Text>

      {/* Hidden native file input */}
      <input
        ref={fileInputRef as any}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={(e: any) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
          e.target.value = ''
        }}
      />

      {phase === 'idle' && (
        <div
          onDragOver={(e: any) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e: any) => {
            e.preventDefault()
            setIsDragging(false)
            const file = e.dataTransfer.files?.[0]
            if (file) handleFile(file)
          }}
          onClick={() => fileInputRef.current?.click()}
          style={{
            borderRadius: 16,
            border: `1px dashed ${(theme.colors as any).primary}`,
            padding: 32,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            backgroundColor: isDragging ? 'rgba(0,0,0,0.03)' : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          <Icon name="uploadCloud" size={64} color={theme.colors.lightGrey} />
          <Text type="body" color={theme.colors.lightGrey}>{t('importContacts.file.drag')}</Text>
          <Button title={t('importContacts.selectFiles')} unfilled isSmall onPress={() => fileInputRef.current?.click()} />
          <Text type="bodySmall" color={theme.colors.lightGrey}>{t('importContacts.file.formats')}</Text>
        </div>
      )}

      {phase === 'parsed' && parsed && (
        <View style={styles.card}>
          <View style={styles.fileRow}>
            <Icon name="upload" size={theme.iconSize.small} color={theme.colors.primary} />
            <Text type="bodySmallBold" color={theme.colors.black}>{fileName}</Text>
          </View>
          <Text type="body" color={theme.colors.primary} style={styles.count}>
            {t('importContacts.file.detected', { count: validRowCount })}
          </Text>

          <Text type="bodySmall" color={theme.colors.lightGrey}>{t('importContacts.file.columnsDetected')}</Text>
          <View style={styles.chips}>
            {detectedFields.map((f) => (
              <View key={f} style={styles.chip}>
                <Icon name="check" size={theme.iconSize.extraSmall} color={theme.colors.green} />
                <Text type="bodyExtraSmall" color={theme.colors.black}>{t(FIELD_LABEL_KEYS[f])}</Text>
              </View>
            ))}
          </View>

          {validRowCount === 0 && (
            <Text type="bodySmall" color={theme.colors.error} style={styles.subtitle}>
              {t('importContacts.file.noUsableRows')}
            </Text>
          )}

          <View style={styles.actions}>
            <Button
              title={t('importContacts.file.importButton', { count: validRowCount })}
              onPress={handleImport}
              isSmall
              disabled={validRowCount === 0}
            />
            <Button title={t('common.cancel')} onPress={reset} unfilled isSmall />
          </View>
        </View>
      )}

      {phase === 'importing' && (
        <View style={styles.card}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text type="body" color={theme.colors.black} style={styles.center}>
            {t('importContacts.file.importing', { done: progress.done, total: progress.total })}
          </Text>
          <View style={styles.progressTrack}>
            <View
              style={[
                styles.progressFill,
                { width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` },
              ]}
            />
          </View>
        </View>
      )}

      {phase === 'done' && (
        <View style={styles.card}>
          <Icon name="check" size={48} color={theme.colors.green} />
          <Text type="bodyBold" color={theme.colors.black} style={styles.center}>
            {t('importContacts.file.doneTitle', { count: result.created })}
          </Text>
          {(result.skipped > 0 || result.failed > 0) && (
            <Text type="bodySmall" color={theme.colors.lightGrey} style={styles.center}>
              {t('importContacts.file.doneDetails', { skipped: result.skipped, failed: result.failed })}
            </Text>
          )}
          <Button title={t('importContacts.file.importAnother')} onPress={reset} unfilled isSmall />
        </View>
      )}

      {error && <Text type="bodySmall" color={theme.colors.error} style={styles.subtitle}>{error}</Text>}
    </View>
  )
}

const getStyles = (theme: Theme) => ({
  wrapper: {
    width: '100%' as const,
    maxWidth: 860,
    alignSelf: 'center' as const,
    marginTop: theme.spacing.xxl,
    gap: theme.spacing.sm,
  },
  titleRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing.sm,
  },
  subtitle: { marginBottom: theme.spacing.sm },
  card: {
    borderRadius: theme.border.large,
    borderWidth: 1,
    borderColor: theme.colors.grey10,
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
    alignItems: 'center' as const,
  },
  fileRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing.sm,
  },
  count: { marginTop: -theme.spacing.xs },
  chips: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: theme.spacing.sm,
    justifyContent: 'center' as const,
  },
  chip: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing.xs,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
    borderRadius: theme.border.small,
    backgroundColor: theme.colors.grey10,
  },
  actions: {
    flexDirection: 'row' as const,
    gap: theme.spacing.md,
    marginTop: theme.spacing.xs,
  },
  center: { textAlign: 'center' as const },
  progressTrack: {
    width: '100%' as const,
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.colors.grey10,
    overflow: 'hidden' as const,
  },
  progressFill: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.colors.primary,
  },
})
